import React from "react";
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from "recharts";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import type { ChartConfig } from "@/components/ui/chart";
import { useTranslation } from "react-i18next";

const breakdownChartConfig = {
    count: { label: "Count", color: "hsl(var(--primary))" },
    label: { color: "hsl(var(--primary-foreground))" },
} satisfies ChartConfig;

const PX_PER_CHAR = 7;
const LABEL_PADDING_PX = 16;
const MIN_CHARS = 4;

export type BreakdownBucket = { label: string; count: number; prevCount?: number };

type BarLabelEntry = {
    viewBox?: { x: number; y: number; width: number; height: number };
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    payload?: { label?: string; count?: number };
    value?: string;
};

function truncateByWidth(fullLabel: string, barWidthPx: number): string {
    const maxChars = Math.max(MIN_CHARS, Math.floor((barWidthPx - LABEL_PADDING_PX) / PX_PER_CHAR));
    if (fullLabel.length <= maxChars) return fullLabel;
    return `${fullLabel.slice(0, maxChars - 1)}…`;
}

function BarLabelContent(props: BarLabelEntry): React.ReactElement {
    const vb = props.viewBox;
    const x = vb?.x ?? props.x ?? 0;
    const y = vb?.y ?? props.y ?? 0;
    const width = vb?.width ?? props.width ?? 0;
    const height = vb?.height ?? props.height ?? 0;
    const fullLabel = (props.payload?.label ?? props.value ?? "") as string;
    const display = truncateByWidth(fullLabel, width);
    return (
        <text
            x={x + 8}
            y={y + height / 2}
            textAnchor="start"
            dominantBaseline="middle"
            className="fill-[var(--color-label)]"
            style={{ fontSize: 12 }}
        >
            <title>{fullLabel}</title>
            {display}
        </text>
    );
}

type BreakdownChartContextValue = {
    data: BreakdownBucket[];
    totalCurrent: number;
    totalPrev?: number;
};

const BreakdownChartContext = React.createContext<BreakdownChartContextValue>({
    data: [],
    totalCurrent: 0,
});

type CountLabelEntry = {
    viewBox?: { x: number; y: number; width: number; height: number };
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    value?: number;
    index?: number;
};

function CountLabelContent(props: CountLabelEntry): React.ReactElement {
    const { data, totalCurrent, totalPrev } = React.useContext(BreakdownChartContext);
    const vb = props.viewBox;
    const x = (vb?.x ?? props.x ?? 0) + (vb?.width ?? props.width ?? 0) + 8;
    const y = (vb?.y ?? props.y ?? 0) + (vb?.height ?? props.height ?? 0) / 2;
    const count = (props.value ?? 0) as number;
    const row = props.index !== undefined ? data[props.index] : undefined;
    const prevCount = row?.prevCount;
    const deltaPct =
        prevCount !== undefined &&
        totalPrev != null &&
        totalPrev > 0 &&
        totalCurrent > 0
            ? (count / totalCurrent - prevCount / totalPrev) * 100
            : null;
    return (
        <text
            x={x}
            y={y}
            textAnchor="start"
            dominantBaseline="middle"
            className="fill-foreground"
            style={{ fontSize: 12 }}
        >
            <tspan>{count}</tspan>
            {deltaPct !== null && deltaPct !== 0 && (
                <tspan
                    style={{
                        fontSize: 10,
                        fill: deltaPct > 0 ? "rgb(var(--success))" : "rgb(var(--danger))",
                    }}
                >
                    {" "}
                    {deltaPct > 0 ? "+" : ""}
                    {deltaPct.toFixed(1)}%
                </tspan>
            )}
        </text>
    );
}

export type BreakdownBarChartCardProps = {
    title: string;
    description: string;
    data: BreakdownBucket[];
    totalCurrent: number;
    totalPrev?: number;
    footerText: string;
    footerSubText?: string;
    /** Translation key prefix for tooltip, e.g. "mentees.charts" or "mentors.charts". */
    tooltipKeyPrefix: string;
};

function BreakdownBarTooltipContent(props: {
    active?: boolean;
    payload?: Array<{ payload?: BreakdownBucket }>;
    label?: string;
    tooltipKeyPrefix: string;
}): React.ReactElement | null {
    const { totalCurrent, totalPrev } = React.useContext(BreakdownChartContext);
    const { active, payload, tooltipKeyPrefix } = props;
    const { t } = useTranslation();
    if (!active || !payload || payload.length === 0) return null;
    const point = payload[0]?.payload as BreakdownBucket | undefined;
    if (!point) return null;
    const { count, prevCount } = point;
    return (
        <div className="rounded-md border border-border bg-bg-card px-3 py-2 text-sm shadow-md">
            {point.label != null ? (
                <div className="font-semibold text-text-1">{point.label}</div>
            ) : null}
            <div className="mt-1 space-y-1 text-text-2">
                <p className="leading-none">
                    {t(`${tooltipKeyPrefix}.tooltipThisSeason`, { count, total: totalCurrent })}
                </p>
                {prevCount !== undefined && totalPrev != null && (
                    <p className="leading-none">
                        {t(`${tooltipKeyPrefix}.tooltipLastSeason`, {
                            count: prevCount,
                            total: totalPrev,
                        })}
                    </p>
                )}
            </div>
        </div>
    );
}

export function BreakdownBarChartCard({
    title,
    description,
    data,
    totalCurrent,
    totalPrev,
    footerText,
    footerSubText,
    tooltipKeyPrefix,
}: BreakdownBarChartCardProps): React.ReactElement {
    const contextValue: BreakdownChartContextValue = React.useMemo(
        () => ({ data, totalCurrent, totalPrev }),
        [data, totalCurrent, totalPrev]
    );
    return (
        <Card className="border border-border bg-bg-card shadow-sm">
            <CardHeader>
                <CardTitle className="text-xl font-semibold text-text-1">{title}</CardTitle>
                <CardDescription className="text-sm text-text-2">{description}</CardDescription>
            </CardHeader>
            <CardContent>
                <BreakdownChartContext.Provider value={contextValue}>
                    <ChartContainer config={breakdownChartConfig} className="h-[220px] w-full">
                        <BarChart
                            accessibilityLayer
                            data={data}
                            layout="vertical"
                            margin={{ right: 56 }}
                        >
                            <CartesianGrid horizontal={false} />
                            <YAxis
                                dataKey="label"
                                type="category"
                                tickLine={false}
                                tickMargin={10}
                                axisLine={false}
                                hide
                            />
                            <XAxis dataKey="count" type="number" hide />
                            <ChartTooltip
                                cursor={false}
                                content={
                                    <BreakdownBarTooltipContent
                                        tooltipKeyPrefix={tooltipKeyPrefix}
                                    />
                                }
                            />
                            <Bar
                                dataKey="count"
                                fill="var(--color-count)"
                                radius={4}
                            >
                                <LabelList
                                    dataKey="label"
                                    position="insideLeft"
                                    offset={8}
                                    content={<BarLabelContent />}
                                />
                                <LabelList
                                    dataKey="count"
                                    position="right"
                                    offset={8}
                                    content={<CountLabelContent />}
                                />
                            </Bar>
                        </BarChart>
                    </ChartContainer>
                </BreakdownChartContext.Provider>
            </CardContent>
            <CardFooter className="flex flex-col items-start gap-0 text-sm">
                <p className="text-text-2 leading-none">{footerText}</p>
                {footerSubText && (
                    <p className="text-text-2 leading-none text-text-3 text-xs">{footerSubText}</p>
                )}
            </CardFooter>
        </Card>
    );
}
